// useProfileDownload.ts
import { computed, onMounted, Ref, ref, watch, watchEffect } from "vue";
import { Participant, Project, Recipients } from "../../types/PersologOnlineTypes.ts";
import { getPdfUrl, openPdf } from "../PdfUtils.ts";
import { ProfileContractorAPI } from "../../api";
import { getDisplayName } from "../GeneralUtils.ts";
import { i18n } from "../../main.ts";

export default function useProfileDownload(participant: Ref<Participant>, project: Ref<Project>) {
    const loading = ref(false);

    const languages = ref<string[]>([]);
    const impersonated = localStorage.getItem("impersonatedAccount") !== null;
    const viewable = !impersonated && project.value.recipients !== Recipients.PARTICIPANT;
    const isDisabled = computed(
        () =>
            !(participant.value.status === "GENERATED" || participant.value.status === "NOTIFIED") ||
            project.value.recipients === Recipients.PARTICIPANT,
    );
    const items = ref(setItems());

    async function getPdf() {
        loading.value = true;
        const { objectUrl, filename } = await getPdfUrl(
            project.value.trainer as string,
            project.value.product,
            participant.value.id as number,
            project.value.language,
        );
        openPdf(objectUrl, filename, participant.value.id as number);
        loading.value = false;
    }

    async function getPdfByLanguage(language: string) {
        loading.value = true;
        const { objectUrl, filename } = await getPdfUrl(
            project.value.trainer as string,
            project.value.product,
            participant.value.id as number,
            language,
        );
        openPdf(objectUrl, filename, participant.value.id as number);
        loading.value = false;
    }

    function setItems() {
        return languages.value.map((lang) => ({
            label: getDisplayName(i18n.global.locale.value, lang),
            command: async () => {
                await getPdfByLanguage(lang);
            },
        }));
    }

    async function fetchLanguages() {
        languages.value = await ProfileContractorAPI.fetchLanguages(
            project.value.product,
            participant.value.id as number,
        );
    }

    watchEffect(async () => {
        items.value = setItems();
    });

    watch(
        () => participant,
        async function (newParticipant) {
            if (newParticipant) {
                await fetchLanguages();
            }
        },
    );

    watch(
        () => participant.value.status,
        async (newStatus) => {
            if (newStatus === "GENERATED" || newStatus === "NOTIFIED") {
                await fetchLanguages();
            }
        },
    );

    onMounted(async () => {
        await fetchLanguages();
    });

    return {
        loading,
        viewable,
        getPdf,
        getPdfByLanguage,
        isDisabled,
        items,
        fetchLanguages,
    };
}
